import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight, Download, Loader2, CheckCircle2, Globe, Sparkles,
  BarChart3, MessageSquare, PlayCircle, Video, Compass, Users, Send,
  ShieldCheck, ScrollText, CalendarCheck, Activity, Layers, ExternalLink,
  Clock3, ClipboardList, Wand2,
} from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { DemoTour, SectionNav } from "@/components/landing/TeamLeaderDemo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { icon: ShieldCheck, title: "Vetted partners", body: "Every partner is screened and approved before they represent your team — no one gets a branded page by signing up." },
  { icon: ScrollText, title: "Compliance built in", body: "Approved copy, required disclosures, and NMLS attribution baked into every asset. Nothing goes out unreviewed." },
  { icon: CalendarCheck, title: "Accountability cadence", body: "A weekly Field Update, a monthly growth meeting, and a quarterly strategy review — so momentum doesn't depend on motivation." },
  { icon: Activity, title: "Tracking you can see", body: "Every page visit, lead, and application click attributed to the officer who earned it." },
  { icon: BarChart3, title: "Metrics that matter", body: "Who's producing, what's converting, and where the gaps are — visible to you, not buried in someone's inbox." },
  { icon: Layers, title: "The full toolkit", body: "Branded landing pages, approved social assets, outreach scripts, email templates, and print collateral — issued in one click." },
];

const RHYTHM = [
  { cadence: "Weekly", icon: PlayCircle, title: "The Field Update", body: "A 3-minute brief in four parts: message of the week, a copy/paste script, a compliance reminder, and one action item." },
  { cadence: "Monthly", icon: Video, title: "Partner Growth Meeting", body: "A live 45-minute meeting: program update, compliance alignment, the monthly marketing theme, one growth topic, and wins." },
  { cadence: "Quarterly", icon: Compass, title: "Strategy Review", body: "A 60-minute leadership review: what's converting, how partners are performing, and where the program goes next." },
];

export default function TeamLeaders() {
  const formRef = useRef(null);

  useEffect(() => {
    document.title = "For MLO Team Leaders — Great Rate Check";
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-linen text-navy" data-testid="teamleaders-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-navy-deep/95 backdrop-blur border-b border-white/10">
        <div className="container-grc flex items-center justify-between py-3">
          <a href="/" className="inline-flex items-center rounded-xl bg-white px-3 py-1.5 shadow-soft">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <a
              href={`${API}/teamleaders-onepager.pdf`}
              data-testid="tl-nav-onepager"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-lime transition-colors"
            >
              <Download size={15} /> One-pager
            </a>
            <button
              onClick={scrollToForm}
              data-testid="tl-nav-cta"
              className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-sm px-5 py-2.5 rounded-full shadow-cta transition-all hover:-translate-y-0.5"
            >
              Book an intro call <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-navy-deep text-white relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-teal/25 blur-3xl" aria-hidden />
        <div className="absolute -bottom-28 -right-16 w-96 h-96 rounded-full bg-lime/10 blur-3xl" aria-hidden />
        <div className="container-grc relative py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold tracking-[0.22em] uppercase text-lime">For MLO team leaders &amp; branch managers</p>
            <h1 className="mt-5 font-heading font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-balance" data-testid="tl-hero-heading">
              Give your MLOs a system, <span className="text-lime italic">not another portal.</span>
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
              A done-for-you conversation engine for every loan officer on your team — branded pages,
              weekly done-for-you marketing, live coaching, and the visibility to see exactly who's using it.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={scrollToForm}
                data-testid="tl-hero-cta"
                className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold px-7 py-3.5 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
              >
                Talk to us about your team <ArrowRight size={16} />
              </button>
              <a
                href={`${API}/teamleaders-onepager.pdf`}
                data-testid="tl-hero-onepager"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-lime text-white hover:text-lime font-medium px-6 py-3.5 rounded-full transition-colors"
              >
                <Download size={16} /> Download the one-pager
              </a>
            </div>
            <p className="mt-6 text-sm text-white/50">15 minutes a week per MLO is full participation. We do the rest.</p>
          </motion.div>
        </div>
      </section>

      {/* Day one */}
      <section className="section-y">
        <div className="container-grc">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">How the program actually runs</p>
          <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl max-w-2xl text-balance">
            Six things that make this a system, not a swag bag.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map(({ icon: Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="rounded-3xl bg-white border border-linen-border shadow-soft p-6"
                data-testid={`tl-pillar-${i}`}
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-teal-light text-teal">
                  <Icon size={20} />
                </span>
                <h3 className="mt-4 font-heading font-medium text-lg leading-snug">{title}</h3>
                <p className="mt-2 text-sm text-ink-600 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 15-minute week */}
      <section className="pb-6">
        <div className="container-grc">
          <div className="rounded-3xl bg-navy text-white p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-lime/15 blur-3xl" aria-hidden />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-lime font-bold">The 15-minute week</p>
                <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl leading-snug text-balance" data-testid="tl-week-heading">
                  "We send them exactly what to say. They just hit send."
                </h2>
                <p className="mt-4 text-white/70 leading-relaxed">
                  Every week, each MLO gets a 3-minute Field Update — the message of the week, one
                  copy-paste outreach script, one confidence check, and one clear action item. Between
                  updates, AI Copy Assist writes any custom outreach in seconds. Your busiest producer
                  can fully participate without slowing down.
                </p>
              </div>
              <div className="rounded-2xl bg-white/[0.06] border border-white/10 p-6 backdrop-blur-sm">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-lime">Sample weekly ask</div>
                <ul className="mt-4 space-y-3">
                  {[
                    "Read the 3-minute brief (video optional)",
                    "Copy the script, swap in a name, send it",
                    "Complete one action item — e.g. text three agents",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-white/85">
                      <CheckCircle2 size={16} className="text-lime shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-[12px] text-white/50">That's it. That's the whole week.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confidence + visibility */}
      <section className="section-y pt-10">
        <div className="container-grc grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-8" data-testid="tl-confidence-card">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-teal-light text-teal">
              <MessageSquare size={20} />
            </span>
            <h3 className="mt-4 font-heading font-medium text-2xl leading-snug">Confidence, not guesswork.</h3>
            <p className="mt-3 text-ink-600 leading-relaxed">
              Every script and talking point is approved language — no rate promises, clean handoffs,
              licensed pros handling the numbers. Your team always knows exactly what they can say,
              so nobody improvises something risky.
            </p>
          </div>
          <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-8" data-testid="tl-visibility-card">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-lime text-navy-deep">
              <BarChart3 size={20} />
            </span>
            <h3 className="mt-4 font-heading font-medium text-2xl leading-snug">Visibility for you.</h3>
            <p className="mt-3 text-ink-600 leading-relaxed">
              Page views, lead conversion, packet sends and opens — tracked per MLO. See who's active
              and who's gone quiet at a glance, without nagging anyone. The program's rhythm and
              recognition do the re-engaging for you.
            </p>
          </div>
        </div>
      </section>

      {/* Rhythm */}
      <section className="pb-16">
        <div className="container-grc">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">The operating rhythm</p>
          <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl max-w-2xl text-balance">
            Most programs hand your team a login and disappear.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {RHYTHM.map(({ cadence, icon: Icon, title, body }, i) => (
              <motion.div
                key={cadence}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="rounded-3xl bg-white border border-linen-border shadow-soft p-6"
                data-testid={`tl-rhythm-${cadence.toLowerCase()}`}
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-navy text-lime">
                    <Icon size={17} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-teal">{cadence}</span>
                </div>
                <h3 className="mt-4 font-heading font-medium text-lg">{title}</h3>
                <p className="mt-2 text-sm text-ink-600 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl bg-teal-light/60 border border-teal/20 px-6 py-4 flex items-start gap-3" data-testid="tl-bdm-callout">
            <Users size={18} className="text-teal shrink-0 mt-0.5" />
            <p className="text-sm text-navy leading-relaxed">
              <strong>Bonus for your recruiters:</strong> every MLO gets a partner (BDM) track — a way to
              build their own referral force of agents and connectors underneath them. Your team doesn't
              just get marketing; they get a growth system.
            </p>
          </div>
        </div>
      </section>

      {/* Live walkthrough — nothing gated, jump links for Zoom demos */}
      <SectionNav />
      <DemoTour onBook={scrollToForm} />

      {/* Inquiry form */}
      <section ref={formRef} className="pb-20">
        <div className="container-grc">
          <TeamLeadForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-deep text-white/60 py-10">
        <div className="container-grc flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="inline-flex items-center rounded-xl bg-white/95 px-3 py-1.5">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
          </div>
          <a href={`${API}/teamleaders-onepager.pdf`} data-testid="tl-footer-onepager" className="inline-flex items-center gap-2 text-sm hover:text-lime transition-colors">
            <Download size={14} /> Download the one-pager
          </a>
        </div>
        <div className="container-grc mt-6 text-[11px] leading-relaxed text-white/40 max-w-3xl">
          Great Rate Check · NEXA Mortgage LLC. All mortgage origination activity is performed by licensed
          mortgage professionals. Participation, earnings, and partnership structures are subject to program
          approval and applicable regulations, including RESPA.
        </div>
      </footer>
    </div>
  );
}

function TeamLeadForm() {
  const [form, setForm] = useState({ full_name: "", brokerage_name: "", agent_count: "", email: "", phone: "", city_state: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.brokerage_name.trim() || !form.agent_count || !form.email.trim() || !form.phone.trim()) {
      setErr("Please fill in your name, team name, team size, email, and phone.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/brokerage-inquiries`, { ...form, inquiry_type: "teamlead" });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1.5 w-full rounded-xl border border-white/15 bg-white/[0.07] p-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-lime transition-colors";
  const labelCls = "text-xs uppercase tracking-widest text-white/60 font-medium";

  if (done) {
    return (
      <div className="rounded-3xl bg-navy text-white p-10 sm:p-14 text-center" data-testid="tl-form-success">
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-lime text-navy-deep mx-auto">
          <CheckCircle2 size={26} />
        </span>
        <h3 className="mt-5 font-heading font-medium text-3xl">Got it — talk soon.</h3>
        <p className="mt-3 text-white/70 max-w-lg mx-auto leading-relaxed">
          We received your inquiry and sent a confirmation to your email. We'll reach out shortly to
          set up a short intro call about your team.
        </p>
        <a href={`${API}/teamleaders-onepager.pdf`} className="mt-6 inline-flex items-center gap-2 text-lime font-semibold hover:underline">
          <Download size={15} /> Grab the one-pager while you wait
        </a>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-navy text-white p-8 sm:p-12 relative overflow-hidden" data-testid="tl-form">
      <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-teal/20 blur-3xl" aria-hidden />
      <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <p className="text-[11px] uppercase tracking-[0.2em] text-lime font-bold">Book an intro call</p>
          <h2 className="mt-3 font-heading font-medium text-3xl leading-snug text-balance">
            Tell us about your team.
          </h2>
          <p className="mt-3 text-white/70 leading-relaxed text-sm">
            A short, no-pressure conversation about your roster, your goals, and whether the program
            fits. We'll bring the demo — you bring the questions.
          </p>
        </div>
        <form onSubmit={submit} className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className={labelCls}>Your name *</span>
            <input value={form.full_name} onChange={set("full_name")} data-testid="tl-field-name" className={inputCls} placeholder="Alex Rivera" />
          </label>
          <label className="block">
            <span className={labelCls}>Team / company name *</span>
            <input value={form.brokerage_name} onChange={set("brokerage_name")} data-testid="tl-field-team" className={inputCls} placeholder="Rivera Lending Team" />
          </label>
          <label className="block">
            <span className={labelCls}>How many MLOs? *</span>
            <select value={form.agent_count} onChange={set("agent_count")} data-testid="tl-field-count" className={inputCls}>
              <option value="" className="text-navy">Select…</option>
              {["2–5", "6–15", "16–40", "41–100", "100+"].map((o) => (
                <option key={o} value={o} className="text-navy">{o}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>City / State</span>
            <input value={form.city_state} onChange={set("city_state")} data-testid="tl-field-city" className={inputCls} placeholder="Tampa, FL" />
          </label>
          <label className="block">
            <span className={labelCls}>Email *</span>
            <input type="email" value={form.email} onChange={set("email")} data-testid="tl-field-email" className={inputCls} placeholder="you@company.com" />
          </label>
          <label className="block">
            <span className={labelCls}>Phone *</span>
            <input value={form.phone} onChange={set("phone")} data-testid="tl-field-phone" className={inputCls} placeholder="(425) 555-1234" />
          </label>
          <label className="block sm:col-span-2">
            <span className={labelCls}>Anything we should know? (optional)</span>
            <textarea value={form.message} onChange={set("message")} rows={3} data-testid="tl-field-message" className={inputCls} placeholder="Current tools, goals, timing…" />
          </label>
          {err && <p className="sm:col-span-2 text-sm text-red-300" data-testid="tl-form-error">{err}</p>}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="tl-form-submit"
              className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover disabled:opacity-60 text-navy-deep font-semibold px-7 py-3.5 rounded-full shadow-cta transition-all hover:-translate-y-0.5"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
              Request my intro call
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
